/**
 * AIButton — bottone riusabile per chiamate AI o long-running async (Batch 41-bugfix BUG-18).
 *
 * Comportamento:
 *  - Click → disabled + spinner + label dinamica (loadingLabel || "Generazione in corso...")
 *  - Doppio-click prevenuto: se busy, ignora ulteriori click
 *  - onClick risolve → toast verde (successLabel || "Operazione completata") + reset
 *  - onClick fallisce → toast rosso con messaggio errore (estratto da response.data.detail)
 *
 * Props:
 *  - onClick: () => Promise (richiesto)
 *  - label: string (richiesto)
 *  - loadingLabel?: string
 *  - successLabel?: string | null  (null → no toast)
 *  - errorPrefix?: string  (default "Errore")
 *  - icon?: ReactNode
 *  - variant?: "primary" | "secondary" | "ghost" | "danger" | "outline-primary"
 *  - size?: "sm" | "default"
 *  - disabled?: boolean
 *  - className?: string
 *  - dataTestid?: string
 *  - showSuccessToast?: boolean (default true)
 */
import { useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CircleNotch as Spinner } from "@phosphor-icons/react";

const VARIANT_CLASSES = {
  primary: "bg-[#1B4FD8] hover:bg-[#0D1F3C] text-white",
  secondary: "bg-emerald-600 hover:bg-emerald-700 text-white",
  danger: "bg-red-600 hover:bg-red-700 text-white",
  ghost: "bg-transparent hover:bg-[#1B4FD8]/5 text-[#1B4FD8]",
  "outline-primary": "border border-[#1B4FD8]/40 text-[#1B4FD8] hover:bg-[#1B4FD8]/5 bg-transparent",
};

export default function AIButton({
  onClick,
  label,
  loadingLabel = "Generazione in corso...",
  successLabel,
  errorPrefix = "Errore",
  icon,
  variant = "primary",
  size = "default",
  disabled = false,
  className = "",
  dataTestid,
  showSuccessToast = true,
  loading = false,
  ...rest
}) {
  const [busy, setBusy] = useState(false);

  const handleClick = useCallback(async (e) => {
    if (busy || disabled) return;
    if (e?.preventDefault) e.preventDefault();
    setBusy(true);
    try {
      const result = await onClick();
      if (showSuccessToast && successLabel !== null) {
        toast.success(successLabel || `${label} completato`);
      }
      return result;
    } catch (err) {
      // Estrai messaggio errore in modo robusto
      const detail = err?.response?.data?.detail
        || err?.response?.data?.message
        || err?.message
        || "Errore inatteso";
      toast.error(`${errorPrefix}: ${detail}`);
      // eslint-disable-next-line no-console
      console.error(`[AIButton] ${label} failed:`, err);
      throw err;
    } finally {
      setBusy(false);
    }
  }, [busy, disabled, onClick, successLabel, label, errorPrefix, showSuccessToast]);

  const variantCls = VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary;
  // BUG-44: stato busy interno OR loading esterno (per long-running polling tasks)
  const effectiveBusy = busy || loading;
  const isDisabled = effectiveBusy || disabled;

  return (
    <Button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      size={size}
      className={`gap-2 transition-opacity ${variantCls} ${isDisabled ? "opacity-60 cursor-not-allowed" : ""} ${className}`}
      data-testid={dataTestid}
      aria-busy={effectiveBusy ? "true" : "false"}
      {...rest}
    >
      {effectiveBusy
        ? (<><Spinner size={14} weight="bold" className="animate-spin" />{loadingLabel}</>)
        : (<>{icon}{label}</>)}
    </Button>
  );
}
